<template>
  <div class="socket-editor-container">
    <quill-editor
      :mixStr="curDocs.id"
      v-if="!preview && curDocs && curDocs.id"
      @editor-ready="editorReady"
      @text-change="textChange"
      @selection-change="selectionChange"
      @execute-sync="handleSyncMessageCache"
      @send-message="handleMessageChange"/>

    <quill-editor
      v-if="preview && curDocs && curDocs.id"
      :mixStr="curDocs.id"
      @editor-ready="editorReady"/>

  </div>
</template>

<script>
import Quill from 'quill'
import QuillEditor from '../components/QuillEditor'
import IMSDK from 'ciim_sdk'
import { Date, Util } from '../../../common'
import { getImInitParams } from '../../../services/websocket'
import _ from 'lodash'

import {
  isSyncing,
  cacheLock,
  chineseLock,
  ownOperationQueue,
  receiveOperationQueue,
  curCacheOps,
  syncMessageCache,
  handleOwnOperationQueue,
  getSocketContent
} from '../../../mixin/docs'

import {
  transform,
  compose,
  getOpsRetainLength,
  getOpsRetainObj
} from '../../../../lib'
import MessageCache from '../../../cache'

const webSocketHost = process.env.WEB_SOCKET_HOST
const imHost = process.env.IM_API_DOMAIN
const Delta = Quill.import('delta')
const initCache = new MessageCache('init')

export default {
  name: 'SocketEditor',

  components: {
    QuillEditor
  },
  props: {
    preview: {
      type: Boolean,
      default: false
    },
    curDocs: {
      type: Object,
      default: () => {}
    },
    appkey: {
      type: String,
      default: ''
    },
    channel: {
      type: Number,
      default: -1
    },
    accountId: {
      type: String,
      default: ''
    },
    pGuid: {
      type: String,
      default: ''
    },
    pImToken: {
      type: String,
      default: ''
    },
    updateMessage: {
      type: Array,
      default: () => []
    },
    blockId: {
      type: String,
      default: ''
    },
    tokenDomain: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      imParams: {},
      curIndex: 0,
      curRev: 0,
      quill: {},
      cacheUuid: '',
      content: [],
      imInitStatus: true,
      initLoading: true,
      receiveOwnMessage: false, // 是否收到自己发的消息
      ownSendMessage: false, // 自己是否发消息
      messageQueue: [], // 收到的消息队列
      changeDataClone: [] // 收到的消息的changeData的备份
    }
  },

  created () {
    // console.log('socketEditor-created', this.preview, this.curDocs)
  },

  computed: {
    initMessage () {
      return this.messageTemplateCreate('CLIENT_READY')
    }
  },

  watch: {
    updateMessage (massage) {
      if (!massage) return
      this.handleSocketMessage(massage)
    },
    preview (val) {
      if (!val) {
        this.initWebSocket()
      } else {
        if (this.$im) {
          this.$im.leaveRoom(this.curDocs.id, (code, msg) => {
            console.log('主动离开文档块房间', code, msg)
          })
        }
      }
    }
  },

  methods: {
    initDocs () {
      if (this.curDocs && this.curDocs.content) {
        this.curRev = this.curDocs.baseRev
        isSyncing(true)
        try {
          this.content = JSON.parse(this.curDocs.content)
        } catch (e) {
          console.warn(e)
        }

        if (this.content && this.content.length > 0) {
          this.quill.setContents(this.content)
        }
        isSyncing(false)
      }

      this.$emit('editor-ready', this.quill)
    },
    textChange (params) {
      this.$emit('text-change', params)
    },
    selectionChange (selection) {
      this.$emit('selection-change', selection)
    },
    editorReady (quill) {
      this.quill = quill
      this.initDocs()
      if (!this.preview) {
        this.initWebSocket()
      }
    },

    /**
     * 初始化im服务
     * @return {Promise<void>}
     */
    async initWebSocket () {
      if (!this.accountId) console.log('没有accountId')

      const mParams = await getImInitParams(this.tokenDomain, this.accountId)
      this.imParams = mParams && mParams.data && mParams.data.data

      if (!this.imParams || !this.imParams.token) {
        console.log('获取文档块token失败')
        return
      }

      // 初始化IM
      this.$im = new IMSDK.IMService(this.imParams.appkey, this.imParams.app_secret)
      this.$im.init(webSocketHost, `${imHost}/im_api`)

      // 是否开启调试
      this.$im.setDebug(false)

      // 监听连接成功
      this.$im.setConnectListener(() => console.log('文档块连接成功'))

      // 监听连接失败
      this.$im.setNetFailListener(() => console.log('文档块连接失败'))

      // 登录状态监听，断网重连会调用多次,需要在登录前设置
      this.$im.setLoginStatusListener((code, msg) => {
        if (code !== 200) {
          this.imInitStatus = false
          console.log('登录文档块im出错', code, msg)
          return
        }

        // 进入房间
        this.$im.enterRoom(this.curDocs.id, (code, msg) => {
          if (code !== 200) {
            this.imInitStatus = false
            console.log('进入文档块房间失败', code, msg)
            return
          }

          // 监听发送消息是否成功
          this.$im.sendRoomMsg(this.initMessage, (code) => {
            console.log('发送文档块消息状态', code)
            if (code !== 200) {
            }
          })

          this.initLoading = false
        })

        // 监听离开房间
        this.$im.setRoomMemberLeftListener((info) => {
          this.$emit('room-member-left', info)
        })

        // 监听进入房间
        this.$im.setRoomMemberJoinListener((info) => {
          this.$emit('room-member-join', info)
        })

        this.initLoading = false
      })

      // 登录im
      this.$im.login(this.accountId, this.imParams.token, 301)

      // 监听消息回调
      this.$im.setCustomSysListener((data) => {
        let receiveData = getSocketContent(data, this.accountId)
        console.log('文档块消息回调', receiveData)
        if (!receiveData) return

        // USER_ENTER 新用户
        if (receiveData.type === 'USER_ENTER') {
          console.log('新用户进入文档块', receiveData)
          return
        }

        // 初始化成功
        if (receiveData.type === 'RECONNECT_READY') {
          console.log('文档块初始化完成', receiveData)
          this.curRev = receiveData.newRev
          this.$emit('editor-ready', this.quill)
          initCache.get().length > 0 && this.handleMessageChange(initCache.get()[0]) // 处理初始化完成之前缓存的操作
          return
        }

        this.handleSocketMessage(receiveData)
      })
    },

    handleSocketMessage (receiveData) {
      // 这里有空的时候优化一下，这里是先push再排序的，
      // 其实可以在push的时候直接push到正确的位置
      // 那么这里就有多种排序方式了，以后优化的时候搞一个最优的
      this.messageQueue.push(receiveData)
      this.messageQueue = _.sortBy(this.messageQueue, 'newRev')
      if (this.messageQueue.length > 0) this.webSocketMessage(this.messageQueue[0]) // 从消息队列的第一个消息开始执行同步处理
    },

    webSocketMessage (curReceiveData) {
      console.log('进入文档块内部同步流程', curReceiveData)
      if (curReceiveData.newRev - this.curRev !== 1) return
      isSyncing(true) // 开始同步，增加同步锁
      this.curRev = curReceiveData.newRev // 更新操作次数
      this.$emit('receive-message', curReceiveData)
      // ACCEPT_COMMIT 自己发消息成功后的回调
      if (curReceiveData.type === 'ACCEPT_COMMIT') {
        this.$emit('save-success')
        this.receiveOwnMessage = true
        _.remove(ownOperationQueue(), item => item.uuid === curReceiveData.uuid) // 维护'影响队列'
        isSyncing(false) // 同步结束，解除同步锁

        this.handleCacheOperations() // 收到自己的消息就处理缓存池里面的操作
      }

      // NEW_CHANGES 接收到他人更新
      if (curReceiveData.type === 'NEW_CHANGES') {
        let changeData = curReceiveData.changeset
        this.changeDataClone = _.cloneDeep(changeData)

        if (chineseLock()) receiveOperationQueue().push(changeData)
        // 先判断自己是否发消息，如果没有发的话，永远都是直接同步curCacheOps
        // this.quill.enable(false) // 同步的时候禁止用户输入
        if (this.ownSendMessage) {
          if (!this.receiveOwnMessage && curCacheOps().length > 0) {
            this.updateCacheOperation(_.cloneDeep(changeData)) // 更新当前缓存的操作
          }

          curReceiveData = this.handleSyncOperations(curReceiveData)
        }

        this.handleSyncMessage(curReceiveData, 'normal') // 用处理好的changeData做同步操作
        // this.quill.enable(true) // 同步结束恢复用户输入
        isSyncing(false) // 同步结束，解除同步锁
      }

      this.messageQueue.shift() // 执行完本轮同步后，弹出消息队列的第一项
      if (this.messageQueue.length > 0) this.webSocketMessage(this.messageQueue[0]) // 然后开始执行消息队列剩下的消息
    },

    handleCacheOperations () {
      cacheLock(false) // 处理缓存消息前一刻解除缓存锁

      if (curCacheOps() && curCacheOps().length > 0) {
        console.log('缓存的操作', _.cloneDeep(curCacheOps()))
        this.handleMessageChange(compose(curCacheOps()), true)
        console.log('影响完的缓存的操作', _.cloneDeep(curCacheOps()))
      } else {
        console.log('没有缓存的操作')
      }
    },

    messageTemplateCreate (type, data) {
      let baseTemp = {
        id: this.curDocs.id,
        docId: this.curDocs.id,
        type: type,
        appToken: this.imParams.app_token,
        docsAppToken: this.pImToken,
        guid: this.pGuid,
        blockId: this.blockId,
        send_time: Date.getNowTime(),
        collabId: this.accountId,
        collaboratorInfo: {
          appkey: this.appkey,
          channel: this.channel,
          account_id: this.accountId
        },
        data: data
      }

      return JSON.stringify(baseTemp)
    },

    handleSyncMessageCache (deltaData) {
      if (syncMessageCache().length === 0) return

      console.log('处理中文缓存队列开始', _.cloneDeep(syncMessageCache()))
      // 注意：这个map和下面那个map不能合在一起，因为在同步这个队列的过程中有
      // 可能当前客户端会继续输入中文，此时会阻断同步这个队列，导致有消息会被遗
      // 留在里面，但是这个队列里面的每一条消息都应该被上次输入的中文影响，如果
      // 将两个map合并写的话剩下的消息将不能被上次输入的中文所影响
      syncMessageCache().map(item => {
        this.transformRetainEqual(item.changeset, deltaData)
      })
      console.log('处理中文缓存队列结束', _.cloneDeep(syncMessageCache()))

      this.handleSyncMessage(syncMessageCache()[0])
    },

    /**
     * 执行消息的同步
     * @param curReceiveData 要被同步的消息
     * @param origin 消息的来源
     */
    handleSyncMessage (curReceiveData, origin) {
      const condition = syncMessageCache().length > 0 && origin === 'normal'

      if (chineseLock() || condition) {
        syncMessageCache().push(curReceiveData)
        return
      }

      if (syncMessageCache().length > 0 && curReceiveData.newRev > syncMessageCache()[0].newRev) return

      console.log('执行同步操作', curReceiveData.changeset)
      isSyncing(true) // 开始同步，增加同步锁
      curReceiveData.changeset.map((simpleChange) => {
        let syncDelta = new Delta()
        simpleChange.map((singleOperation, index) => {
          if (singleOperation.retain) {
            syncDelta.retain(singleOperation.retain, singleOperation.attributes)
          }
          if (singleOperation.insert) {
            syncDelta.insert(singleOperation.insert, singleOperation.attributes)
          }
          if (singleOperation.delete) {
            syncDelta.delete(singleOperation.delete)
          }
        })
        this.quill.updateContents(syncDelta)
      })

      isSyncing(false) // 同步结束，解除同步锁
      _.remove(syncMessageCache(), item => item.uuid === curReceiveData.uuid) // 执行完本轮同步后，弹出被同步过的
      console.log('执行完同步后的syncMessageCache', _.cloneDeep(syncMessageCache()))

      if (syncMessageCache().length > 0) this.handleSyncMessage(syncMessageCache()[0])
    },

    handleMessageChange (message, isCacheOps) {
      if (!message) return
      let cache = initCache.get()

      const isArray = Array.isArray(message)

      // 缓存所有数据
      if (isArray || !cache.some(item => item.id === message.id)) {
        initCache.register({
          deltaData: isArray ? message : message.deltaData,
          isCacheOps,
          id: initCache.createUuid()
        })
      }
      cache && cache.length > 0 && this.remoteMessageSend(cache[0].deltaData, cache[0].isCacheOps)
    },

    /**
     * 执行发送消息
     * @param deltaData 需要被处理的数据
     * @param isCacheOps 是否是缓存消息
     */
    remoteMessageSend (deltaData, isCacheOps) {
      // 优先抛出事件，因为外层要第一时间拿到变化，尽管后面可能会出错
      this.$emit('send-message', deltaData)

      if (deltaData.length === 0 || deltaData[0].length === 0) return
      if (typeof isCacheOps === 'undefined') isCacheOps = false

      let data = {
        baseRev: this.curRev,
        isCacheOps: isCacheOps,
        uuid: isCacheOps && this.cacheUuid ? this.cacheUuid : Util.getUUID(),
        changeset: deltaData
      }
      let message = this.messageTemplateCreate('USER_CHANGES', data)
      this.ownSendMessage = true // 自己发送了消息
      this.receiveOwnMessage = false // 只要自己发送了信息，就把是否收到自己信息的标志改为false

      ownOperationQueue().push({
        uuid: data.uuid,
        data: deltaData
      })

      if (isCacheOps) {
        this.cacheUuid = '' // 重置uuid
        curCacheOps([]) // 清空缓存
      }

      this.$im && this.$im.sendRoomMsg(message, (code) => {
        console.log('发送消息状态以及消息', code, message)
        if (code === 200) {
          // 发送成功的话抛出事件
          this.$emit('send-success')

          // 执行完本轮同步后，弹出消息队列的第一项
          initCache.get().shift()

          // 即使发送失败也要处理继续剩下的消息，这里不能完全确定缓存池里
          // 面还会不会有消息，因为可能在正在处理的过程中用户又进行了操作。
          this.handleMessageChange(initCache.get()[0])
        }
      })

      // 发送消息后立刻加上缓存锁
      cacheLock(true)
    },

    /**
     * 在影响本地缓存之前需要用影响队列去影响一下本次收到的同步消息
     * @param changeData 需要被处理的数据
     */
    updateCacheOperation (changeData) {
      ownOperationQueue().map(item => {
        changeData = transform(changeData, ...item.data)
      })

      curCacheOps(compose(curCacheOps())) // 合并缓存
      console.log('此时的缓存是：', _.cloneDeep(curCacheOps()))
      if (curCacheOps().length > 0) transform(curCacheOps(), ...changeData, true) // 影响缓存
      console.log('解决完冲突后的缓存', curCacheOps())
    },

    /**
     * 在同步之前需要处理一下收到的消息
     * @param curReceiveData 需要被处理的数据
     */
    handleSyncOperations (curReceiveData) {
      // 同步之前看一下是否有新的缓存（这是在收到消息之前到收到消息之后客户端产生的
      // 新的缓存消息，它对于此刻的消息同步也是有影响的），所以有的话要加入影响队列
      if (curCacheOps().length > 0) {
        if (!this.cacheUuid) this.cacheUuid = Util.getUUID()
        ownOperationQueue().push({
          uuid: this.cacheUuid,
          data: compose(curCacheOps())
        })
      }

      if (ownOperationQueue().length > 0) {
        console.log('当前的ownOperationQueue', _.cloneDeep(ownOperationQueue()))
        // 对当前的影响队列进行处理，包括去重、去杂质消息，修改多个缓存基于的版本一致性
        let ownQueue = handleOwnOperationQueue()
        console.log('处理之后的ownOperationQueue', _.cloneDeep(ownOperationQueue()))

        // 用影响队列去影响changeData，使得changeData改变为基于当前客户端状态的消息
        ownQueue.map(item => {
          curReceiveData.changeset = this.transformRetainEqual(curReceiveData.changeset, ...item.data)
        })

        // 用没有被改变过的changeData（this.changeDataClone）去影响没有被影响过的影响队列
        // 这是为保持本地的影响队列是基于收到本条消息之后的版本，因为此时服务端也对下一条消息
        // 做了同样的操作
        ownOperationQueue().map(item => {
          transform(item.data, ...this.changeDataClone)
        })
      }

      return curReceiveData
    },

    /**
     * 由于业务原因，需要某些情况下retain长度一样也要作为冲突解决
     * @param baseOpsCollection 受影响的集合
     * @param operationNext 将影响的数据
     * @param debug 是否开启调试
     */
    transformRetainEqual (baseOpsCollection, operationNext, debug) {
      let curOps = _.cloneDeep(operationNext)
      let curOpsRetainObj = getOpsRetainObj(curOps)
      if (getOpsRetainLength(baseOpsCollection[0]) === getOpsRetainLength(operationNext)) curOpsRetainObj.retain--
      return transform(baseOpsCollection, curOps)
    }
  }
}
</script>

<style lang="scss" scoped>
  @import "../../../style/base";
  .socket-editor-container {
    height: 100%;
    width: 100%;
    position: relative;
  }
</style>



// WEBPACK FOOTER //
// src/components/quill/components/SocketEditor.vue